/**
 * useDeleteAccount — passthrough hook for AccountDeletionPort (033 US2,
 * T029).
 *
 * Reads the accountDeletion port from PortsContext and returns a callback
 * that forwards to `port.requestDeletion()`. Throws a clear error when
 * the provider or the port slot is absent (this surface cannot degrade —
 * an erasure affordance that silently does nothing would be the P7
 * fake-continuity smell).
 *
 * Mutation-only, mirroring `useUpdateContactDetails`: no Suspense read,
 * stable identity via `useMemo` keyed on the port.
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 * Contract: specs/033-.../contracts/my-data-api.md.
 */

import { useContext, useMemo } from 'react';
import { PortsContext } from '../providers/PortsContext.js';

export type DeleteAccount = () => Promise<{ completedAt: string }>;

export function useDeleteAccount(): DeleteAccount {
  const ports = useContext(PortsContext);

  if (ports === null) {
    throw new Error(
      'useDeleteAccount: PortsContext not found. Make sure the component is wrapped in a PortsProvider.',
    );
  }

  if (ports.accountDeletion === undefined) {
    throw new Error(
      'useDeleteAccount: accountDeletion port is not registered. This feature requires the account-deletion port to be configured.',
    );
  }

  const port = ports.accountDeletion;

  // react-compiler-exception: callback identity must stay stable per port so consumers can use it as an effect dependency
  return useMemo<DeleteAccount>(() => () => port.requestDeletion(), [port]);
}
